/**
 * ExitStationModal — replaces $rootScope.exitStationModal (templates/exit_station_modal.html).
 * Triggered by geofence exit events from useBackgroundGeo.
 */

import { Button, NumberInput, Select, Stack, Text } from '@mantine/core';
import { useState } from 'react';
import { ModalFrame } from './ModalFrame';
import type { Station, Transfer } from '@/types';

interface Props {
  opened: boolean;
  stations: Station[];
  initialStationId?: number | null;
  onSave: (transfer: Transfer) => Promise<void>;
  onClose: () => void;
}

export function ExitStationModal({ opened, stations, initialStationId, onSave, onClose }: Props) {
  const [stationId, setStationId] = useState<number | null>(initialStationId ?? null);
  const [weight, setWeight] = useState<number | string>('');
  const [totalPaid, setTotalPaid] = useState<number | string>('');
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [wasOpened, setWasOpened] = useState(opened);

  // The modal stays mounted, so pick up the geofence station each time it opens
  if (opened !== wasOpened) {
    setWasOpened(opened);
    if (opened) setStationId(initialStationId ?? null);
  }

  const stationOptions = stations.map(s => ({ value: String(s.id), label: s.name }));

  const handleSave = async () => {
    const w = Number(weight);
    const p = Number(totalPaid);
    const sid = stationId;

    if (!sid || sid <= 0 || w <= 0 || p <= 0) {
      setErrorMsg(
        'Please make sure you have selected a transfer station and entered the weight and total paid.',
      );
      return;
    }

    setSaving(true);
    setErrorMsg(null);
    try {
      await onSave({ stationid: sid, weight: w, total_paid: p });
      onClose();
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalFrame
      opened={opened}
      onClose={onClose}
      title="Exit Transfer Station"
      closeOnClickOutside={false}
      actions={
        <>
          <Button variant="default" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button color="green" onClick={handleSave} loading={saving}>
            Save Transfer
          </Button>
        </>
      }
    >
      {/* The NumberInputs hide their step buttons, which are half the Touch floor. A
          weight or an amount is typed, not stepped. */}
      <Stack gap="sm">
        <Select
          label="Transfer Station"
          placeholder="Select station"
          data={stationOptions}
          value={stationId ? String(stationId) : null}
          onChange={val => setStationId(val ? Number(val) : null)}
          required
        />

        <NumberInput
          label="Weight (kg)"
          placeholder="Enter weight"
          value={weight}
          onChange={setWeight}
          min={0}
          hideControls
          required
        />

        <NumberInput
          label="Total Paid ($)"
          placeholder="Enter amount"
          value={totalPaid}
          onChange={setTotalPaid}
          min={0}
          hideControls
          required
        />

        {errorMsg && <Text c="red">{errorMsg}</Text>}
      </Stack>
    </ModalFrame>
  );
}
